import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Sparkles, ArrowDownRight, ArrowUpRight, Eye } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/signals')({
  component: SignalsPage,
});

function SignalsPage() {
  const q = useQuery({
    queryKey: ['signals'],
    queryFn: api.signals,
    refetchInterval: 5 * 60_000,
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-4"
    >
      <div>
        <h1 className="text-xl font-semibold text-zinc-100 tracking-tight">Signals</h1>
        <p className="text-[12px] text-zinc-400 mt-0.5">
          Indicator + rule fusion. Surfaces candidates only, never auto-executes.
        </p>
      </div>

      {!q.data ? (
        <div className="text-sm text-zinc-400">Loading…</div>
      ) : q.data.signals.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-800 p-8 text-center text-zinc-400 text-sm">
          No signals fired right now.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {q.data.signals.map((s) => {
            const Icon =
              s.kind === 'BUY_CANDIDATE'
                ? ArrowUpRight
                : s.kind === 'SELL_CANDIDATE'
                  ? ArrowDownRight
                  : Eye;
            const tone =
              s.kind === 'BUY_CANDIDATE'
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : s.kind === 'SELL_CANDIDATE'
                  ? 'border-rose-500/40 bg-rose-500/5'
                  : 'border-indigo-500/40 bg-indigo-500/5';
            return (
              <Link
                to="/positions/$ticker"
                params={{ ticker: s.ticker }}
                key={s.id}
                className={cn(
                  'block rounded-xl border p-4 hover:border-opacity-80 transition-colors',
                  tone,
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="size-4 text-zinc-300" />
                    <span className="font-mono text-sm text-zinc-100">
                      {s.ticker.split('_')[0]}
                    </span>
                  </div>
                  <span className="text-[11px] uppercase text-zinc-400">{s.kind.replace('_', ' ')}</span>
                </div>
                <p className="text-xs text-zinc-300 text-pretty leading-snug">{s.reason}</p>

                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800/60">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="size-3 text-indigo-300" />
                    <span className="text-[11px] text-zinc-400">Score</span>
                    <span className="text-[11px] num font-medium text-zinc-100">{s.score}</span>
                  </div>
                  <div className="ml-auto">
                    {s.passesRules ? (
                      <span className="text-[10px] uppercase text-emerald-400 tracking-wider">
                        Rules pass
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase text-amber-400 tracking-wider">
                        {s.failingRules.length} rule blocked
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
